import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import type { ChangeEvent } from 'react';

import {
  Box,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import { ColorPicker } from 'Components/ColorPicker';
import {
  selectCoursesPaginationSettings,
  selectDateTimePickerSettings,
  selectTagColorMode,
  selectTagStyling,
  selectTagVariant,
  setDateTimeDateFormat,
  setDateTimeDisplayMode,
  setDateTimeTimeFormat,
  setDateTimeTimeStep,
  setListPaginationPageSize,
  setTagBorderSize,
  setTagColorMode,
  setTagFontSize,
  setTagUniformColor,
  setTagVariant
} from 'Config/configSlice';
import {
  BORDER_SIZE_RANGE,
  FONT_SIZE_RANGE,
  TAG_COLOR_MODE_OPTIONS,
  TAG_VARIANT_OPTIONS,
  TagColorMode,
  TagVariant
} from 'Config/tagsConfig';
import { getCourses } from 'Courses/data';
import {
  decodeTagArray,
  QUERY_PARAM_EXCLUDE_TAGS,
  QUERY_PARAM_INCLUDE_TAGS,
  QUERY_PARAM_SEARCH
} from 'Courses/hooks/urlSyncUtils';
import { useAppDispatch, useAppSelector } from 'Store';
import type { DateFormat, DisplayMode, TimeFormat } from 'Utils/dateTime';
import { DATE_FORMAT_OPTIONS, DISPLAY_MODE_OPTIONS, TIME_FORMAT_OPTIONS } from 'Utils/dateTime';

import { IconSizes, SettingsIcon } from '@libs/shared/ui/icons';

const MIN_PAGE_SIZE = 1;
const MAX_PAGE_SIZE = 10000;

type DevConfigPanelProps = Readonly<{
  showPagination?: boolean;
  panelWidth?: number;
  rightOffset?: string;
}>;

export const DevConfigPanel = ({
  showPagination = false,
  panelWidth = 320,
  rightOffset = '-20em'
}: DevConfigPanelProps) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [isExpanded, setIsExpanded] = useState(false);
  const [pageSizeInputValue, setPageSizeInputValue] = useState('');

  const paginationSettings = useAppSelector(selectCoursesPaginationSettings);
  const tagVariant = useAppSelector(selectTagVariant);
  const tagColorMode = useAppSelector(selectTagColorMode);
  const tagStyling = useAppSelector(selectTagStyling);
  const dateTimePickerSettings = useAppSelector(selectDateTimePickerSettings);

  const currentSearchText = (searchParams.get(QUERY_PARAM_SEARCH) ?? '').trim();
  const currentIncludeTags = decodeTagArray(searchParams.get(QUERY_PARAM_INCLUDE_TAGS));
  const currentExcludeTags = decodeTagArray(searchParams.get(QUERY_PARAM_EXCLUDE_TAGS));

  useEffect(() => {
    if (showPagination) {
      setPageSizeInputValue(paginationSettings.pageSize.toString());
    }
  }, [paginationSettings.pageSize, showPagination]);

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handlePageSizeInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPageSizeInputValue(value);
  };

  const handlePageSizeBlur = () => {
    const newSize = parseInt(pageSizeInputValue, 10);
    if (!isNaN(newSize) && newSize >= MIN_PAGE_SIZE && newSize <= MAX_PAGE_SIZE) {
      dispatch(setListPaginationPageSize({ listName: 'courses', pageSize: newSize }));

      dispatch(
        getCourses({
          pageSize: newSize,
          page: 1,
          searchText: currentSearchText || undefined,
          includeTags: currentIncludeTags?.length ? currentIncludeTags : undefined,
          excludeTags: currentExcludeTags?.length ? currentExcludeTags : undefined
        })
      );
    } else {
      setPageSizeInputValue(paginationSettings.pageSize.toString());
    }
  };

  const handleTagVariantChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setTagVariant(event.target.value as TagVariant));
  };

  const handleTagColorModeChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setTagColorMode(event.target.value as TagColorMode));
  };

  const handleUniformColorChange = (color: string) => {
    dispatch(setTagUniformColor(color));
  };

  const handleBorderSizeChange = (_event: Event, value: number | number[]) => {
    dispatch(setTagBorderSize(value as number));
  };

  const handleFontSizeChange = (_event: Event, value: number | number[]) => {
    dispatch(setTagFontSize(value as number));
  };

  const handleDateFormatChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setDateTimeDateFormat(event.target.value as DateFormat));
  };

  const handleTimeFormatChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setDateTimeTimeFormat(event.target.value as TimeFormat));
  };

  const handleDisplayModeChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setDateTimeDisplayMode(event.target.value as DisplayMode));
  };

  const handleTimeStepChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = parseInt((event.target as unknown as HTMLSelectElement).value, 10);
    if (!isNaN(value) && value > 0) {
      dispatch(setDateTimeTimeStep(value));
    }
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 10, right: 10, zIndex: 9999 }}>
      <Tooltip title={isExpanded ? 'Hide Dev Config' : 'Show Dev Config'} placement="left">
        <IconButton
          onClick={handleToggleExpanded}
          sx={{
            backgroundColor: 'lightgray',
            color: 'white',
            '&:hover': { backgroundColor: 'darkgray' },
            mb: isExpanded ? 1 : 0,
            boxShadow: 2,
            width: 30,
            height: 30,
            right: rightOffset
          }}
          size="small"
          aria-label={isExpanded ? 'Hide Dev Config' : 'Show Dev Config'}
        >
          <SettingsIcon fontSize={IconSizes.small} />
        </IconButton>
      </Tooltip>

      <Collapse in={isExpanded}>
        <Paper
          elevation={8}
          sx={{
            p: 2,
            minWidth: panelWidth,
            maxWidth: panelWidth + 80,
            border: '2px solid darkgray',
            backgroundColor: 'lightgray',
            maxHeight: '80vh',
            overflow: 'auto'
          }}
        >
          {showPagination && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Pagination
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    label="page size"
                    type="number"
                    size="small"
                    value={pageSizeInputValue}
                    onChange={handlePageSizeInputChange}
                    onBlur={handlePageSizeBlur}
                    sx={{ width: 100 }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 1.5 }} />
            </>
          )}

          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
              Tags
            </Typography>

            <FormControl size="small">
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Tag Variant
              </Typography>
              <RadioGroup value={tagVariant} onChange={handleTagVariantChange}>
                {TAG_VARIANT_OPTIONS.map(option => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" />}
                    label={option.label}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {(tagVariant === TagVariant.FILLED || tagVariant === TagVariant.OUTLINED) && (
              <FormControl size="small" sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Color Mode
                </Typography>
                <RadioGroup value={tagColorMode} onChange={handleTagColorModeChange}>
                  {TAG_COLOR_MODE_OPTIONS.map(option => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      label={option.label}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                    />
                  ))}
                </RadioGroup>

                {tagColorMode === TagColorMode.UNIFORM && (
                  <Box sx={{ mt: 1 }}>
                    <ColorPicker
                      value={tagStyling.uniformColor}
                      onChange={handleUniformColorChange}
                      label="Uniform Color"
                    />
                  </Box>
                )}
              </FormControl>
            )}

            {(tagVariant === TagVariant.FILLED || tagVariant === TagVariant.OUTLINED) && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {tagVariant === TagVariant.OUTLINED && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                      Border Size: {tagStyling.borderSize}px
                    </Typography>
                    <Slider
                      value={tagStyling.borderSize}
                      onChange={handleBorderSizeChange}
                      min={BORDER_SIZE_RANGE.MIN}
                      max={BORDER_SIZE_RANGE.MAX}
                      step={1}
                      size="small"
                      sx={{ width: '100%' }}
                    />
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                    Font Size: {tagStyling.fontSize}px
                  </Typography>
                  <Slider
                    value={tagStyling.fontSize}
                    onChange={handleFontSizeChange}
                    min={FONT_SIZE_RANGE.MIN}
                    max={FONT_SIZE_RANGE.MAX}
                    step={1}
                    size="small"
                    sx={{ width: '100%' }}
                  />
                </Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
              DateTime Picker
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl size="small">
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Date Format
                </Typography>
                <RadioGroup
                  value={dateTimePickerSettings.dateFormat}
                  onChange={handleDateFormatChange}
                >
                  {DATE_FORMAT_OPTIONS.map(option => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      label={option.label}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              <FormControl size="small">
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Time Format
                </Typography>
                <RadioGroup
                  value={dateTimePickerSettings.timeFormat}
                  onChange={handleTimeFormatChange}
                >
                  {TIME_FORMAT_OPTIONS.map(option => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      label={option.label}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              <FormControl size="small">
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Display Mode
                </Typography>
                <RadioGroup
                  value={dateTimePickerSettings.displayMode}
                  onChange={handleDisplayModeChange}
                >
                  {DISPLAY_MODE_OPTIONS.map(option => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      label={option.label}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              {/* TODO: <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Time Step:{' '}
                  {TIME_STEP_OPTIONS.find(opt => opt.value === dateTimePickerSettings.timeStep)
                    ?.label ?? 'Custom'}
                </Typography>
                <TextField
                  select
                  size="small"
                  value={dateTimePickerSettings.timeStep}
                  onChange={handleTimeStepChange}
                  sx={{ width: '100%' }}
                >
                  {TIME_STEP_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
              </Box> */}
            </Box>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};
